/**
 * Benachrichtigungen erzeugen (F-30) - rein und getestet.
 *
 * Aus einem Schnappschuss der Daten entstehen fuer heute und die naechsten
 * Tage alle Meldungen mit Zeitpunkt. Gegen die Flut gilt:
 * - gebuendelt statt einzeln ("3 Aufgaben ueberfaellig"),
 * - hoechstens eine Meldung je Art und Tag (die ID enthaelt den Tag) -
 *   ausser bei Terminen und Aufgabenbloecken, die ihre eigene Uhrzeit haben,
 * - in der Ruhezeit nichts: was hineinfaellt, kommt gesammelt danach.
 * Dieselben Meldungen fuellen die Glocke (F-30) und den Push-Plan (F-31).
 */
import { unbestaetigt } from '@/domain/calendar/bestaetigung';
import { erinnerungsZeit } from '@/domain/calendar/reminders';
import { deadlineStatus, type DeadlineItem } from '@/domain/deadlines';
import { tagesAuswertung, type HabitDays } from '@/domain/habits/daily';
import { fuerHeute, ueberfaellig } from '@/domain/tasks/faellig';
import { faelligerMonat, faelligeWoche } from '@/domain/review/faellig';
import { addDays, addMinutes, datePart, differenceInDays, endOfMonth, formatDe } from '@/lib/datetime';
import type { MeilensteinItem } from '@/domain/projects/meilensteine';
import type { CalendarOccurrence, Ferien, ID, LocalDate, LocalDateTime, Task } from '@/domain/types';
import { uhrzeitAm, type Einstellungen, type MeldungsArt } from '@/domain/benachrichtigungen/einstellungen';
import { istFreierTag } from '@/domain/calendar/freieTage';

export interface Meldung {
  /** Stabil je Sache und Tag - dieselbe Sache wird nie doppelt gemeldet. */
  id: string;
  art: MeldungsArt;
  /** Wann sie faellig ist - nach der Ruhezeit verschoben. */
  zeit: LocalDateTime;
  titel: string;
  text: string;
  /** Fuer Push mit neutralen Texten: ohne Titel und Inhalte. */
  neutral: string;
  /** Hash-Route, z. B. `/aufgaben`. */
  link: string;
  /** Direkt erledigen: genau eine Aufgabe. */
  aufgabeId?: ID;
}

export interface Schnappschuss {
  heute: LocalDate;
  jetzt: LocalDateTime;
  /** Vorkommen von heute - 7 Tage bis zum Ende des Zeitraums. */
  termine: CalendarOccurrence[];
  deadlines: DeadlineItem[];
  meilensteine: MeilensteinItem[];
  aufgaben: Task[];
  gewohnheiten: HabitDays[];
  /** Vorhandene Journaleintraege als `datum:typ`. */
  journal: Set<string>;
  wochenReflexionen: Set<string>;
  monatsReflexionen: Set<string>;
  /** Tag der letzten Sicherung, `null` = noch nie. */
  letzteSicherung: LocalDate | null;
  /** Offene Wochenziele der aktuellen Woche. */
  wochenziele: string[];
  /** Geplante Trainingseinheiten je Tag (F-34 E2) - fehlt = kein Plan. */
  training?: { tag: LocalDate; einheiten: { name: string; uhrzeit?: string; erledigt: boolean }[] }[];
  /** Ernaehrung heute (F-35 E) - fehlt = nichts melden. */
  ernaehrung?: { tag: LocalDate; getrunkenMl: number; zielMl?: number; mahlzeiten: number; mindest: number };
  /** Feiertage des Bundeslands, gestern bis Planende - freie Tage (F-41). Fehlt = keine. */
  feiertage?: ReadonlySet<LocalDate>;
  /** Ferien aus den Schul-Einstellungen (F-22) - ebenfalls frei (F-41). Fehlt = keine. */
  ferien?: readonly Ferien[];
}

const KEINE_FEIERTAGE: ReadonlySet<LocalDate> = new Set();

/** So viele Tage ab heute werden geplant (heute + 2). */
export const PLAN_TAGE = 3;

const zahl = (n: number, eins: string, viele: string) => `${n} ${n === 1 ? eins : viele}`;
const inTagen = (n: number) => (n === 0 ? 'heute' : n === 1 ? 'morgen' : `in ${n} Tagen`);
const liste = (xs: string[], max = 3) => xs.slice(0, max).join(', ') + (xs.length > max ? ` und ${xs.length - max} weitere` : '');

/** Verschiebt eine Zeit aus der Ruhezeit an deren Ende. */
export function nachRuhezeit(zeit: LocalDateTime, von: string, bis: string): LocalDateTime {
  const t = zeit.slice(11, 16);
  const tag = datePart(zeit);
  if (von === bis) return zeit;
  if (von < bis) return t >= von && t < bis ? `${tag}T${bis}` : zeit;
  if (t >= von) return `${addDays(tag, 1)}T${bis}`;
  if (t < bis) return `${tag}T${bis}`;
  return zeit;
}

function deadlineLink(d: DeadlineItem): string {
  if (d.kind === 'aufgabe') return `/aufgaben?aufgabe=${encodeURIComponent(d.id)}`;
  if (d.kind === 'projekt') return `/projekte/${encodeURIComponent(d.id)}`;
  if (d.kind === 'ziel' || d.kind === 'keyresult') return `/ziele/${encodeURIComponent(d.ownerId)}`;
  return '/schule';
}

export function meldungen(s: Schnappschuss, e: Einstellungen): Meldung[] {
  const out: Meldung[] = [];
  // Nur fuer Termine und Bloecke - die folgen der Zeit des Termins, nicht der Art des Tages.
  const an = (a: MeldungsArt) => e.arten[a].an;
  // Alle anderen: Uhrzeit je Tag (F-41) - an freien Tagen evtl. anders oder `null` = heute keine Meldung.
  const frei = (tag: LocalDate) => istFreierTag(tag, s.ferien ?? [], s.feiertage ?? KEINE_FEIERTAGE);
  const um = (a: MeldungsArt, tag: LocalDate): LocalDateTime | null => {
    const u = uhrzeitAm(e.arten[a], frei(tag));
    return u === null ? null : (`${tag}T${u}` as LocalDateTime);
  };
  const vorlauf = (a: MeldungsArt) => e.arten[a].vorlauf ?? 0;
  // Ab gestern: was gestern in die Ruhezeit fiel, kommt heute morgen.
  const tage = Array.from({ length: PLAN_TAGE + 1 }, (_, i) => addDays(s.heute, i - 1));
  const imZeitraum = (z: LocalDateTime) => datePart(z) >= tage[0]! && datePart(z) <= tage[tage.length - 1]!;
  const ausgefallen = (o: CalendarOccurrence) => o.bestaetigung?.status === 'ausgefallen';

  // Termine: die am Termin eingestellten Erinnerungen (F-04 f).
  if (an('termin')) {
    for (const o of s.termine) {
      if (ausgefallen(o)) continue;
      for (const m of o.reminderMinutes ?? []) {
        const zeit = erinnerungsZeit(o, m);
        if (!imZeitraum(zeit)) continue;
        const uhr = o.allDay ? 'ganztägig' : `um ${formatDe(String(o.start), 'HH:mm')}`;
        const wann = m === 0 ? 'Jetzt' : m >= 1440 ? 'Morgen' : `In ${m >= 60 ? `${m / 60} h` : `${m} min`}`;
        out.push({
          id: `termin:${o.occurrenceId}:${m}`, art: 'termin', zeit, titel: o.title || 'Termin',
          text: `${wann}, ${uhr}${o.location ? ` · ${o.location}` : ''}`,
          neutral: m === 0 ? 'Ein Termin beginnt jetzt' : `Termin ${wann.toLowerCase()}`,
          link: `/kalender?termin=${encodeURIComponent(o.occurrenceId)}&datum=${datePart(String(o.start))}`,
        });
      }
    }
  }

  // Eingeplante Aufgabenbloecke (F-06), nicht wenn schon abgehakt.
  if (an('block')) {
    for (const o of s.termine) {
      if (!o.sourceTaskId || o.allDay || o.blockErledigt || ausgefallen(o)) continue;
      const zeit = addMinutes(o.start as LocalDateTime, -vorlauf('block'));
      if (!imZeitraum(zeit)) continue;
      out.push({
        id: `block:${o.occurrenceId}`, art: 'block', zeit, titel: o.title || 'Aufgabe',
        text: `Eingeplant ab ${formatDe(String(o.start), 'HH:mm')}`, neutral: 'Ein eingeplanter Aufgabenblock beginnt',
        link: `/aufgaben?aufgabe=${encodeURIComponent(o.sourceTaskId)}`, aufgabeId: o.sourceTaskId,
      });
    }
  }

  for (const tag of tage) {
    // Deadlines und Klausuren: gebuendelt je Tag.
    const fristen = (art: 'deadline' | 'klausur', treffer: (n: number) => boolean) => {
      const z = um(art, tag);
      if (!z) return;
      const heute = s.deadlines
        .filter((d) => (art === 'klausur') === (d.kind === 'klausur'))
        .map((d) => ({ d, n: differenceInDays(tag, d.date) }))
        .filter(({ n }) => treffer(n));
      if (heute.length === 0) return;
      const eins = heute.length === 1 ? heute[0]! : null;
      const name = art === 'klausur' ? ['Klausur', 'Klausuren'] : ['Deadline', 'Deadlines'];
      out.push({
        id: `${art}:${tag}`, art, zeit: z,
        titel: eins ? `${name[0]} ${inTagen(eins.n)}: ${eins.d.title}` : `${heute.length} ${name[1]} stehen an`,
        text: liste(heute.map(({ d, n }) => `${d.title} (${inTagen(n)})`)),
        neutral: eins ? `${name[0]} ${inTagen(eins.n)}` : `${heute.length} ${name[1]} stehen an`,
        link: eins ? deadlineLink(eins.d) : art === 'klausur' ? '/schule' : '/kalender',
      });
    };
    fristen('deadline', (n) => n === 0 || n === vorlauf('deadline'));
    fristen('klausur', (n) => n === 1 || n === vorlauf('klausur'));

    const zMeilenstein = um('meilenstein', tag);
    if (zMeilenstein) {
      const steine = s.meilensteine.filter((m) => !m.done).map((m) => ({ m, n: differenceInDays(tag, m.date) }))
        .filter(({ n }) => n === 0 || n === vorlauf('meilenstein'));
      if (steine.length > 0) {
        const eins = steine.length === 1 ? steine[0]! : null;
        out.push({
          id: `meilenstein:${tag}`, art: 'meilenstein', zeit: zMeilenstein,
          titel: eins ? `Meilenstein ${inTagen(eins.n)}: ${eins.m.title}` : `${steine.length} Meilensteine stehen an`,
          text: liste(steine.map(({ m, n }) => `${m.title} – ${m.projektTitel} (${inTagen(n)})`)),
          neutral: eins ? `Meilenstein ${inTagen(eins.n)}` : `${steine.length} Meilensteine stehen an`,
          link: eins ? `/projekte/${encodeURIComponent(eins.m.projectId)}` : '/projekte',
        });
      }
    }

    const zUeberfaellig = um('ueberfaellig', tag);
    if (zUeberfaellig) {
      const alt = ueberfaellig(s.aufgaben, tag);
      if (alt.length > 0) {
        const eins = alt.length === 1 ? alt[0]! : null;
        out.push({
          id: `ueberfaellig:${tag}`, art: 'ueberfaellig', zeit: zUeberfaellig,
          titel: eins ? `Überfällig: ${eins.title}` : `${alt.length} Aufgaben überfällig`,
          text: liste(alt.map((t) => t.title)), neutral: zahl(alt.length, 'Aufgabe überfällig', 'Aufgaben überfällig'),
          link: eins ? `/aufgaben?aufgabe=${encodeURIComponent(eins.id)}` : '/aufgaben',
          ...(eins ? { aufgabeId: eins.id } : {}),
        });
      }
    }

    const zVorbei = um('deadlineVorbei', tag);
    if (zVorbei) {
      const z = zVorbei;
      // Aufgaben stehen schon unter "ueberfaellig" - dieselbe Sache nicht zweimal melden
      // (an diesem Tag; kommt "ueberfaellig" heute nicht, stehen sie hier).
      const vorbei = s.deadlines.filter((d) => d.kind !== 'klausur' && !(d.kind === 'aufgabe' && zUeberfaellig)
        && d.date < tag && deadlineStatus(d.date, d.time, z).state === 'ueberfaellig');
      if (vorbei.length > 0) {
        out.push({
          id: `deadline-vorbei:${tag}`, art: 'deadlineVorbei', zeit: z,
          titel: vorbei.length === 1 ? `Deadline überschritten: ${vorbei[0]!.title}` : `${vorbei.length} Deadlines überschritten`,
          text: liste(vorbei.map((d) => d.title)), neutral: zahl(vorbei.length, 'Deadline überschritten', 'Deadlines überschritten'),
          link: vorbei.length === 1 ? deadlineLink(vorbei[0]!) : '/kalender',
        });
      }
    }

    const zGewohnheit = um('gewohnheit', tag);
    if (zGewohnheit) {
      const offen = tagesAuswertung(s.gewohnheiten, tag).offen;
      if (offen.length > 0) {
        out.push({
          id: `gewohnheit:${tag}`, art: 'gewohnheit', zeit: zGewohnheit,
          titel: `${zahl(offen.length, 'Gewohnheit', 'Gewohnheiten')} heute noch offen`,
          text: liste(offen.map((h) => h.name)), neutral: `${zahl(offen.length, 'Gewohnheit', 'Gewohnheiten')} heute noch offen`,
          link: '/gewohnheiten',
        });
      }
    }

    for (const [art, typ, name] of [['journalMorgen', 'morgen', 'Morgenjournal'], ['journalAbend', 'abend', 'Abendjournal']] as const) {
      const z = um(art, tag);
      if (!z || s.journal.has(`${tag}:${typ}`)) continue;
      out.push({
        id: `${art}:${tag}`, art, zeit: z, titel: `${name} noch nicht ausgefüllt`,
        text: typ === 'abend' ? 'Ein paar Minuten für Bewertung, Reflexion und das Bestätigen der Termine.' : 'Wie war die Nacht, wofür bist du dankbar?',
        neutral: `${name} noch nicht ausgefüllt`, link: `/journal?datum=${tag}&typ=${typ}`,
      });
    }

    const zBestaetigen = um('bestaetigen', tag);
    if (zBestaetigen) {
      const z = zBestaetigen;
      const offen = unbestaetigt(s.termine.filter((o) => String(o.end) <= z), addDays(tag, -7), z);
      if (offen.length > 0) {
        out.push({
          id: `bestaetigen:${tag}`, art: 'bestaetigen', zeit: z,
          titel: `${zahl(offen.length, 'Termin', 'Termine')} noch nicht bestätigt`,
          text: 'Stattgefunden, ausgefallen oder anders gelaufen?', neutral: `${zahl(offen.length, 'Termin', 'Termine')} noch nicht bestätigt`,
          link: `/journal?datum=${datePart(String(offen[0]!.start))}&typ=abend`,
        });
      }
    }

    const zReflexion = um('reflexion', tag);
    if (zReflexion) {
      // Gleiche Regel wie auf der Uebersicht (F-40): Rueckblick-Tag und die zwei Tage danach.
      const kw = faelligeWoche(tag, e.rueckblickTag);
      if (kw) {
        if (!s.wochenReflexionen.has(kw)) {
          out.push({
            id: `reflexion:${kw}:${tag}`, art: 'reflexion', zeit: zReflexion, titel: 'Wochenreflexion steht aus',
            text: `KW ${kw.slice(-2)} – Rückblick und Planung der nächsten Woche.`, neutral: 'Wochenreflexion steht aus',
            link: `/rueckblick?woche=${kw}`,
          });
        }
      }
      // Monatsletzter und die zwei Tage danach.
      const monat = faelligerMonat(tag);
      if (monat && !s.monatsReflexionen.has(monat)) {
        const letzter = endOfMonth(`${monat}-01`);
        out.push({
          id: `reflexion:${monat}:${tag}`, art: 'reflexion', zeit: zReflexion, titel: 'Monatsreflexion steht aus',
          text: `${formatDe(letzter, 'MMMM yyyy')} – Rückblick und Planung des nächsten Monats.`, neutral: 'Monatsreflexion steht aus',
          link: `/rueckblick?monat=${monat}`,
        });
      }
    }

    const zSicherung = um('sicherung', tag);
    // Ein Datum in der Zukunft (Uhr ging einmal vor) zaehlt als ueberfaellig.
    const seitSicherung = s.letzteSicherung ? differenceInDays(s.letzteSicherung, tag) : null;
    if (zSicherung && (seitSicherung === null || seitSicherung < 0 || seitSicherung >= vorlauf('sicherung'))) {
      out.push({
        id: `sicherung:${tag}`, art: 'sicherung', zeit: zSicherung, titel: 'Datensicherung überfällig',
        text: seitSicherung === null ? 'Noch nie gesichert – deine Daten liegen nur in diesem Browser.'
          : seitSicherung < 0 ? 'Die letzte Sicherung trägt ein Datum in der Zukunft.' : `Letzte Sicherung vor ${seitSicherung} Tagen.`,
        neutral: 'Datensicherung überfällig', link: '/einstellungen',
      });
    }

    // Training (F-34 E2): morgens die Einheiten des Tages, abends, was fehlt.
    const einheiten = s.training?.find((t) => t.tag === tag)?.einheiten ?? [];
    const offen = einheiten.filter((x) => !x.erledigt);
    const name = (x: (typeof einheiten)[number]) => (x.uhrzeit ? `${x.name} (${x.uhrzeit})` : x.name);
    const zTraining = um('training', tag);
    if (zTraining && offen.length > 0) {
      out.push({
        id: `training:${tag}`, art: 'training', zeit: zTraining,
        titel: offen.length === 1 ? `Heute Training: ${name(offen[0]!)}` : `Heute ${zahl(offen.length, 'Einheit', 'Einheiten')}`,
        text: liste(offen.map(name)), neutral: 'Heute steht Training an', link: '/training',
      });
    }
    const zTrainingOffen = um('trainingOffen', tag);
    if (zTrainingOffen && offen.length > 0) {
      out.push({
        id: `training-offen:${tag}`, art: 'trainingOffen', zeit: zTrainingOffen,
        titel: `${zahl(offen.length, 'Einheit', 'Einheiten')} heute noch nicht absolviert`,
        text: `${liste(offen.map(name))} – starten oder als Lauf/Nachtrag eintragen.`, neutral: 'Eine Trainingseinheit ist noch offen', link: '/training',
      });
    }

    // Ernaehrung (F-35 E, standardmaessig aus): nur fuer heute - der Stand anderer Tage ist noch offen.
    const ern = s.ernaehrung?.tag === tag ? s.ernaehrung : undefined;
    const zTrinken = um('trinken', tag);
    if (ern && zTrinken && ern.zielMl && ern.getrunkenMl < ern.zielMl / 2) {
      const l = (ml: number) => `${(ml / 1000).toLocaleString('de-DE', { maximumFractionDigits: 2 })} l`;
      out.push({
        id: `trinken:${tag}`, art: 'trinken', zeit: zTrinken, titel: `Erst ${l(ern.getrunkenMl)} getrunken`,
        text: `Trinkziel heute: ${l(ern.zielMl)}.`, neutral: 'Ans Trinken denken', link: '/ernaehrung',
      });
    }
    const zEintragen = um('ernaehrungEintragen', tag);
    if (ern && zEintragen && ern.mahlzeiten < ern.mindest) {
      out.push({
        id: `ernaehrung-eintragen:${tag}`, art: 'ernaehrungEintragen', zeit: zEintragen,
        titel: ern.mahlzeiten ? `Erst ${zahl(ern.mahlzeiten, 'Mahlzeit', 'Mahlzeiten')} eingetragen` : 'Heute noch nichts gegessen eingetragen',
        text: 'Für einen vollständigen Tag fehlen noch Einträge.', neutral: 'Mahlzeiten eintragen', link: '/ernaehrung',
      });
    }

    const zUeberblick = um('tagesueberblick', tag);
    if (zUeberblick) {
      const termine = s.termine.filter((o) => datePart(String(o.start)) === tag && !ausgefallen(o));
      const faellig = fuerHeute(s.aufgaben, tag).length + s.deadlines.filter((d) => d.date === tag).length;
      const teile = [zahl(termine.length, 'Termin', 'Termine'), `${faellig} fällig`];
      const erster = termine.filter((o) => !o.allDay).sort((a, b) => String(a.start).localeCompare(String(b.start)))[0];
      out.push({
        id: `tagesueberblick:${tag}`, art: 'tagesueberblick', zeit: zUeberblick,
        titel: `Dein Tag: ${teile.join(', ')}`,
        text: [
          erster ? `Erster Termin ${formatDe(String(erster.start), 'HH:mm')}: ${erster.title}` : '',
          s.wochenziele.length > 0 ? `Wochenziele: ${liste(s.wochenziele)}` : '',
        ].filter(Boolean).join(' · ') || 'Nichts Festes geplant.',
        neutral: `Dein Tag: ${teile.join(', ')}`, link: '/',
      });
    }
  }

  return out
    .map((m) => ({ ...m, zeit: nachRuhezeit(m.zeit, e.ruheVon, e.ruheBis) }))
    .filter((m) => m.zeit >= `${s.heute}T00:00`)
    .sort((a, b) => a.zeit.localeCompare(b.zeit) || a.id.localeCompare(b.id));
}
